import { JSX, useContext } from "react";
import { rootContext } from "../contexts/rootContext";
import SupaLink from "./SupaLink";
import gns3LabImg from "../images/gns3Lab.png";
import thesis2 from "../images/honorsThesis2.png";
import { useNavigate } from "react-router";

type GitLogsProps = {
    smallScreen: boolean;
    openModal: (title: string, titleColor: string, content: JSX.Element) => void;
}

function GitLogs(props: GitLogsProps) {
    const context = useContext(rootContext);
    const theme = context.theme;
    const textSize = props.smallScreen ? "14px" : "18px";
    const navigate = useNavigate()

    type Commit = {
        date: Date;
        msg: string;
        branch: string;
        content?: any;
        link?: string;
        isHead?: boolean;
    }

    const gns3DialogContent = (
        <span>
            {`In 2016 I researched IPv6 security. I created a dual stack (IPv4+IPv6) environment using GNS3 and several Virtual Box machines. I focused on working with Metasploit and the `}
            <a 
                style={{
                    color: context.theme.text.contrast5,
                    cursor: `url(${context.theme.cursor.pointer}), default`
                }} 
                href="https://github.com/vanhauser-thc/thc-ipv6" 
                target="_blank"
            >
                THC IPv6 toolkit
            </a>
            {`.`}
            <p><a 
                style={{
                    color: context.theme.text.contrast5,
                    cursor: `url(${context.theme.cursor.pointer}), default`
                }} 
                href="https://drive.google.com/file/d/0B-DhbiJT0nEyMThZcUZOM1pTb2c/view?resourcekey=0-rp_9zwXnJEDc62ptfTlFkA" 
                target="_blank"
            >
                Lab Source Files (~3GB)
            </a></p>
            <img 
                style={{ width: "100%", marginTop: "15px" }} 
                src={gns3LabImg}
                alt="networking diagram"
            />
        </span>);

    const honorsThesisDialogContent = (
        <span>
            <p>
                {`This project resulted in the creation of a web application that takes in flow data (sFlow, NetFlow, etc.) and outputs interactive 3D visualizations.`}
            </p>
                <iframe width="100%" height="315" src="https://www.youtube.com/embed/dBGhoMTLDuk?si=3aplNVB8YsWUZVoV" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
            <p>
                {`I first worked in conjunction with the University of Arkansas's network team to collect and anonymize flow data from many on-campus switches. Then, I developed a web application that produces interactive visuals using this anonymized data. I built the back-end of the application with Node.js and the visualizations were produced using the BabylonJS framework.`}
            </p>
                <img 
                    style={{ width: "100%", marginTop: "15px" }} 
                    src={thesis2}
                    alt="Brok standing infront of his project poster"
                />
            <p>
                {`I attended the 2018 SEC Cyber Security conference in Auburn, Alabama where I presented my work to academic, industry, and government security professionals. I completed my thesis by defending my work in front of a committee of professors and several members from the University of Arkansas's network security team.`}
            </p>
            <a 
                style={{
                    color: context.theme.text.contrast5,
                    cursor: `url(${context.theme.cursor.pointer}), default`
                }} 
                href="https://github.com/BrokanXYZ/Flow-Visualization" 
                target="_blank"
            >
                Source Code
            </a>
        </span>);

    const itInternDialogContent = (
        <span>
                <b style={{color: theme.text.contrast3}}>Company</b>{`: Windstream`}<br/>
                <b style={{color: theme.text.contrast3}}>Location</b>{`: Little Rock, Arkansas`}<br/>
                <b style={{color: theme.text.contrast3}}>Duration</b>{`: May 2015 -> Aug 2015 | 4 months`}
            <p>
                {`I worked on the Windstream Online customer portal tactical team. I solved issues customers had while on the site. That entailed digging through server logs, querying Oracle databases, and working with several internal and third party teams.`}
            </p>
        </span>);

    const hungryHogsDialogContent = (
        <span>
            <p>
                {`This was my team's project for the 2016 J.B. Hunt Hackathon. We placed 3rd in this competition. The goal for this hackathon was to build a web/mobile application that would provide a benefit for the community. Our idea was to create a website that displays all upcoming campus events that are offering free food/drinks.`}
            </p>
            <p>
                {`Through my sociology teacher's (Rodica Lisnic) research, I've learned that many students at the University of Arkansas face food insecurity. These students do not have reliable access to affordable and nutritious food.`}
            </p>
            <p>
                {`My team and I presented this project in Boston at the 2017 `}
                <a 
                    style={{
                        color: context.theme.text.contrast5,
                        cursor: `url(${context.theme.cursor.pointer}), default`
                    }} 
                    href="https://www.clintonfoundation.org/programs/leadership-public-service/clinton-global-initiative/" 
                    target="_blank"
                >
                    Clinton Global Initiative
                </a>
                {` conference.`}
            </p>
            
        </span>);

    const earthsenseDialogContent = (
        <span>
            <p>
                {`Earthsense was a system of air quality sensors placed around Fayetteville, Arkansas. My teammates were focused on building the hardware, while I was responsible for the server and database that collected and provided access to the sensor data.`}
            </p>
            <p>
                {`I built and maintained the Cassandra DB on an Ubuntu VM, created a Python TCP server that collects data from our air quality sensors, and developed a REST API with Spring Boot that allows users to interface with collected data.`}
            </p>
                <a 
                    style={{
                        color: context.theme.text.contrast5,
                        cursor: `url(${context.theme.cursor.pointer}), default`
                    }} 
                    href="https://github.com/BrokanXYZ/ES-Server" 
                    target="_blank"
                >
                    Source Code
                </a>
            
        </span>);

    const netSecInternDialogContent = (
        <span>
                <b style={{color: theme.text.contrast3}}>Company</b>{`: Windstream`}<br/>
                <b style={{color: theme.text.contrast3}}>Location</b>{`: Little Rock, Arkansas`}<br/>
                <b style={{color: theme.text.contrast3}}>Duration</b>{`: May 2016 -> Aug 2016 | 4 months`}
            <p>
                {`I managed voice and data subpoena entry and distribution to a team of 4 analysts. I researched IP/MAC addresses providing requested data to state and national agencies, including the FBI. I handled user administration for network elements (Cisco and Juniper) across multiple companies and networks. I also created an automation tool with PHP, so that the subpoena team could save time on data entry.`}
            </p>
        </span>);

    const softwareInternDialogContent = (
        <span>
                <b style={{color: theme.text.contrast3}}>Company</b>{`: Green Mountain Technology`}<br/>
                <b style={{color: theme.text.contrast3}}>Location</b>{`: Memphis, Tennessee`}<br/>
                <b style={{color: theme.text.contrast3}}>Duration</b>{`: May 2017 -> Aug 2017 | 4 months`}
            <p>
                {`I worked with C# and Microsoft's micro-service platform, Service Fabric. I created a proof of concept and presented my work to the CEO and several IT teams. I also successfully migrated their first monolithic application to the Service Fabric Platform.`}
            </p>
        </span>);

    const fullStackDialogContent = (
        <span>
                <b style={{color: theme.text.contrast3}}>Company</b>{`: Boeing`}<br/>
                <b style={{color: theme.text.contrast3}}>Location</b>{`: Seattle, Washington`}<br/>
                <b style={{color: theme.text.contrast3}}>Duration</b>{`: Jan 2019 -> May 2021 | 2 years 5 months`}
            <p>
                {`I worked on a team practicing Extreme Programming (love/hate relationship) within the Sales and Marketing space. We developed web applications that allowed internal sales users to manage aircraft order contracts. I became the technical lead on an application that was critical for the 737 MAX return to service. I also contributed to several adjacent services that were owned by the Sales Analytics team.`}
            </p>
        </span>);

    const seniorDevBoeingDialogContent = (
        <span>
                <b style={{color: theme.text.contrast3}}>Company</b>{`: Boeing`}<br/>
                <b style={{color: theme.text.contrast3}}>Location</b>{`: Seattle, Washington`}<br/>
                <b style={{color: theme.text.contrast3}}>Duration</b>{`: Oct 2024 -> PRESENT`}
            <p>
                {`I'm leading development on a new application that will visualize airline networks, range circles, and more for commercial marketing teams. Using ArcGIS technologies, Typescript, C#, Sql Server and more!`}
            </p>
        </span>);

    const pauloDialogContent = (
        <span>
            <p>
                {`I co-founded the Paulo Claudio Foundation with several of my childhood friends in honor of Paulo Claudio. My primary role is as a web developer, but I also help with other areas of work. Paulo was a great friend of ours who passed away from a rare form of cancer. Through this organization, we assist cancer patients in need of travel and lodging while receiving treatments. `}
            </p>
            <a 
                style={{
                    color: context.theme.text.contrast5,
                    cursor: `url(${context.theme.cursor.pointer}), default`
                }} 
                href="https://paulospoints.org/" 
                target="_blank"
            >
                https://paulospoints.org/
            </a>
        </span>);

    const devopsBoeingDialogContent = (
        <span>
                <b style={{color: theme.text.contrast3}}>Company</b>{`: Boeing`}<br/>
                <b style={{color: theme.text.contrast3}}>Location</b>{`: Seattle, Washington`}<br/>
                <b style={{color: theme.text.contrast3}}>Duration</b>{`: Jul 2022 -> Oct 2024 | 2 years 4 months`}
            <p>
                {`I led a DevSecOps initiative across the Sales and Marketing organization. I coached teams on DevSecOps practices, developed DevSecOps tools for those teams, and led production support for several key applications.`}
            </p>
        </span>);

    const awsDialogContent = (
        <span>
                <b style={{color: theme.text.contrast3}}>Company</b>{`: Amazon Web Services (AWS)`}<br/>
                <b style={{color: theme.text.contrast3}}>Location</b>{`: Seattle, Washington`}<br/>
                <b style={{color: theme.text.contrast3}}>Duration</b>{`: May 2021 -> Jun 2022 | 1 year 2 months`}
            <p>
                {`I worked on Amazon Comprehend - a natural language processing cloud service that utilizes machine learning.`}
            </p>
            <p>
                {`I led several front-end projects, made significant back-end contributions, and stayed strong during many sleepless on-call rotations.`}
            </p>
            <a 
                style={{
                    color: context.theme.text.contrast5,
                    cursor: `url(${context.theme.cursor.pointer}), default`
                }} 
                href="https://aws.amazon.com/comprehend" 
                target="_blank"
            >
                AWS Comprehend
            </a>
            
        </span>);

    const arSandboxDialogContent = (
        <span>
            <p>
                {`I designed and developed a 3D melee combat video game integrated with a custom-built Augmented Reality (AR) sandbox. I constructed a physical sandbox using real wood and sand; implemented real-time topographical mapping so terrain changes in the sand (hills, valleys, etc.) were mirrored in the virtual environment.`}
            </p>
            <p>
                {`This game reached a playable prototype stage; development paused upon accepting an offer to work at Boeing.`}
            </p>
            <a 
                style={{
                    color: context.theme.text.contrast5,
                    cursor: `url(${context.theme.cursor.pointer}), default`
                }} 
                href="https://github.com/BrokanXYZ/3D-AR-Sandbox-Video-Game" 
                target="_blank"
            >
                Source Code
            </a>
            <hr style={{ marginBottom: "20px", marginTop: "20px" }} />
            <iframe width="100%" height="315" src="https://www.youtube.com/embed/XsTA7HTHhCU?si=AXECdFGBqSn2XNGH" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
            <hr style={{ marginBottom: "20px", marginTop: "20px" }} />
            <iframe width="100%" height="315" src="https://www.youtube.com/embed/FHelUjj2cQg?si=osYneExkx1_p0jAA" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
            <hr style={{ marginBottom: "20px", marginTop: "20px" }} />
            <iframe width="100%" height="315" src="https://www.youtube.com/embed/6i-1WUPaM6U?si=yloPk1hRArfcDwWT" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
        </span>);

    const commits: Commit[] = [
        {
            date: new Date(2015, 7, 1),
            msg: "IT Intern",
            branch: "Internships",
            content: itInternDialogContent
        },
        {
            date: new Date(2016, 7, 1),
            msg: "Network Security Intern",
            branch: "Internships",
            content: netSecInternDialogContent
        },
        {
            date: new Date(2016, 9, 1),
            msg: "GNS3 Dual Stack Lab",
            branch: "University of Arkansas",
            content: gns3DialogContent
        },
        {
            date: new Date(2016, 9, 1),
            msg: "Hungry Hogs",
            branch: "University of Arkansas",
            content: hungryHogsDialogContent
        },
        {
            date: new Date(2017, 7, 1),
            msg: "Software Developer Intern",
            branch: "Internships",
            content: softwareInternDialogContent
        },
        {
            date: new Date(2018, 3, 1),
            msg: "Capstone: Earthsense",
            branch: "University of Arkansas",
            content: earthsenseDialogContent
        },
        {
            date: new Date(2018, 4, 1),
            msg: "Honors Thesis: 3D Network Visualization",
            branch: "University of Arkansas",
            content: honorsThesisDialogContent
        },
        {
            date: new Date(2018, 4, 1),
            msg: "BS Computer Science | GPA 3.85",
            branch: "University of Arkansas"
        },
        {
            date: new Date(2018, 9, 1),
            msg: "AR Sandbox Game",
            branch: "Personal Projects",
            content: arSandboxDialogContent
        },
        {
            date: new Date(2019, 0, 1),
            msg: "Full Stack Web Developer",
            branch: "Boeing",
            content: fullStackDialogContent
        },
        {
            date: new Date(2020, 4, 1),
            msg: "Co-Founded Organization",
            branch: "Paulo Claudio Foundation",
            content: pauloDialogContent
        },
        {
            date: new Date(2020, 5, 1),
            msg: "Created paulospoints.org",
            branch: "Paulo Claudio Foundation"
        },
        {
            date: new Date(2021, 4, 1),
            msg: "Software Development Engineer",
            branch: "Amazon Web Services",
            content: awsDialogContent
        },
        {
            date: new Date(2022, 6, 1),
            msg: "DevOps Technical Lead",
            branch: "Boeing",
            content: devopsBoeingDialogContent
        },
        {
            date: new Date(2024, 9, 1),
            msg: "Senior Software Developer",
            branch: "Boeing",
            content: seniorDevBoeingDialogContent,
            isHead: true
        },
        {
            date: new Date(2026, 5, 1),
            msg: "Buffalo River Map",
            branch: "Personal Projects",
            link: "/buffalo",
        },
    ]

    commits.reverse();

    const formatDateToYYYYMM = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed, so add 1
        return `${year}-${month}`;
    }

    const getBranchColor = (branch: string) => {
        switch(branch) {
            case "Boeing":
                return theme.text.contrast4
            case "Amazon Web Services":
                return theme.text.contrast1
            case "Paulo Claudio Foundation":
                return theme.text.contrast2
            case "Personal Projects":
                return theme.text.contrast5
            case "University of Arkansas":
                return theme.text.contrast1
            case "Internships":
                return theme.text.contrast2
            default:
                return "black";
        }
    }

    const getCommitDate = (commit: Commit) => (<span style={{ color: theme.text.contrast3}}>{formatDateToYYYYMM(commit.date)}</span>);
    const getCommitBranch = (commit: Commit) => (<span style={{ color: getBranchColor(commit.branch)}}>{commit.branch}</span>);
    const getCommitMsg = (commit: Commit) => {
        if (commit.content != null || commit.link != null) {
            const handleClick = commit.link != null ?
                () => navigate(commit.link!)
                :
                () => props.openModal(commit.msg, getBranchColor(commit.branch),  commit.content)
            return <SupaLink 
                        text={commit.msg} 
                        textSize={textSize} 
                        textColor={getBranchColor(commit.branch)} 
                        handleClick={handleClick} 
                    />
        } else {
            return <span style={{ color: theme.text.primary}}>{commit.msg}</span>
        }
                    
    }
    const getHeadTag = () => (<span>&nbsp;{`(HEAD -> main)`}</span>)

    return (<div
        style={{ 
            marginLeft: "25px",
            marginBottom: "25px"
        }}
    >
        {commits.map(commit => (
            <span
                key={`${commit.msg}-${commit.date.toDateString()}`}
                style={{
                    display: "block",
                    lineHeight: "30px",
                    fontSize: textSize
                }}
            >
                {props.smallScreen ?
                    <>
                        {getCommitDate(commit)}&nbsp;~&nbsp;{getCommitBranch(commit)}{commit.isHead && getHeadTag()}
                        <br/>
                        &nbsp;&nbsp;&nbsp;&nbsp;{getCommitMsg(commit)}<br/>
                    </>
                    :
                    <>
                        {getCommitDate(commit)}
                        &nbsp;~&nbsp;
                        {getCommitBranch(commit)}
                        &nbsp;~&nbsp;
                        {getCommitMsg(commit)}
                        {commit.isHead && getHeadTag()}
                    </>
                }
                
            </span>
        ))}
    </div>)
    
}
    
export default GitLogs;